import {
  ActionIcon,
  Box,
  Button,
  CloseButton,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Title,
  TypographyStylesProvider,
} from '@mantine/core';
import { IconSend } from '@tabler/icons-react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Form } from '@/api';
import { useRustyState } from '../../state';

const PANEL_WIDTH = 380;
// Matches the backend's cap on earlier turns sent along with a question.
const MAX_FOLLOW_UPS = 5;

interface Turn {
  question: string;
  answer: string;
}

function renderMarkdown(markdown: string): string {
  return DOMPurify.sanitize(marked.parse(markdown, { async: false }) as string);
}

export interface ChatAssistantProps {
  /** The form as currently saved; its settings go along with the question. */
  form: Form;
  opened: boolean;
  onToggle: () => void;
}

// The settings the assistant answers from; the form's id travels in the URL.
function formState(form: Form): Record<string, unknown> {
  return {
    title: form.title,
    method: form.method,
    filter_spam: form.filter_spam,
    check_challenge: form.check_challenge,
    challenge_algorithm: form.challenge_algorithm,
    redirect_url: form.redirect_url,
    check_specs: form.check_specs,
    specs: form.specs,
    auto_response_enabled: form.auto_response_enabled,
    auto_response_subject: form.auto_response_subject,
    auto_response_text: form.auto_response_text,
    email_notification_format: form.email_notification_format,
    auto_response_format: form.auto_response_format,
    retention_days: form.retention_days ?? null,
    allowed_origins: form.allowed_origins ?? [],
    encryption_mode: form.encryption_mode,
    email_notification_type: form.email_notification_type,
  };
}

export function ChatAssistant({ form, opened, onToggle }: ChatAssistantProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: follows the answer as it streams in
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [answer, turns]);

  useEffect(() => {
    if (opened && !isStreaming && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [opened, isStreaming]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isStreaming || turns.length > MAX_FOLLOW_UPS) return;

    const currentInput = input.trim();
    setQuestion(currentInput);
    setInput('');
    setAnswer('');
    setError(null);
    setIsStreaming(true);

    const api = useRustyState.getState().api;
    let full = '';

    await api.formChat(
      form.id,
      currentInput,
      formState(form),
      turns,
      (content: string) => {
        full += content;
        setAnswer(full);
      },
      () => {
        setIsStreaming(false);
        setTurns((prev) => [...prev, { question: currentInput, answer: full }]);
        setQuestion('');
        setAnswer('');
      },
      (errorType: string) => {
        setIsStreaming(false);
        setError(errorType);
      }
    );
  }, [input, isStreaming, form, turns]);

  const handleNewConversation = useCallback(() => {
    setTurns([]);
    setQuestion('');
    setAnswer('');
    setError(null);
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const turnsHtml = useMemo(() => turns.map((turn) => renderMarkdown(turn.answer)), [turns]);
  const answerHtml = useMemo(() => (answer ? renderMarkdown(answer) : ''), [answer]);
  const canAsk = !isStreaming && turns.length <= MAX_FOLLOW_UPS;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  if (!opened) {
    return null;
  }

  return (
    <Paper
      shadow="sm"
      withBorder
      component="aside"
      aria-label={t('form.assistant.title')}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === 'Escape' && !e.defaultPrevented) onToggle();
      }}
      style={{
        width: PANEL_WIDTH,
        minWidth: PANEL_WIDTH,
        height: 'calc(100dvh - 160px)',
        position: 'sticky',
        top: 76,
        alignSelf: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Group
        justify="space-between"
        p="sm"
        style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
      >
        <Title order={5}>{t('form.assistant.title')}</Title>
        <CloseButton onClick={onToggle} aria-label={t('ui.close')} />
      </Group>
      <Text size="xs" c="dimmed" px="sm" pt="xs">
        {t('form.assistant.external')}
      </Text>

      <ScrollArea style={{ flex: 1 }} viewportRef={scrollRef} p="sm">
        {turns.map((turn, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: turns are only ever appended
          <Box key={i} mb="md">
            <Text size="sm" c="dimmed" mb="xs" fs="italic">
              {turn.question}
            </Text>
            <TypographyStylesProvider fz="sm">
              {/* biome-ignore lint/security/noDangerouslySetInnerHtml: DOMPurify-sanitised markdown */}
              <div dangerouslySetInnerHTML={{ __html: turnsHtml[i] }} />
            </TypographyStylesProvider>
          </Box>
        ))}
        {question && (
          <Text size="sm" c="dimmed" mb="xs" fs="italic">
            {question}
          </Text>
        )}
        {answer && (
          <TypographyStylesProvider fz="sm">
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: DOMPurify-sanitised markdown */}
            <div dangerouslySetInnerHTML={{ __html: answerHtml }} />
          </TypographyStylesProvider>
        )}
        {error && (
          <Text size="sm" c="red">
            {error === 'rateLimit' ? t('chat.rateLimit') : t('chat.error')}
          </Text>
        )}
        {isStreaming && !answer && (
          <Group gap="xs">
            <Loader size="xs" type="dots" />
            <Text size="sm" c="dimmed">
              {t('chat.thinking')}
            </Text>
          </Group>
        )}
        {turns.length === 0 && !question && !error && (
          <Text size="sm" c="dimmed">
            {t('chat.placeholder')}
          </Text>
        )}
        {turns.length > MAX_FOLLOW_UPS && (
          <Text size="sm" c="dimmed">
            {t('chat.followUpLimit')}
          </Text>
        )}
      </ScrollArea>

      <Box p="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
        <Stack gap="xs">
          {(turns.length > 0 || error) && !isStreaming && (
            <Button variant="light" size="xs" fullWidth onClick={handleNewConversation}>
              {t('chat.newQuestion')}
            </Button>
          )}
          {canAsk && (
            <Group gap="xs" align="flex-end">
              <Textarea
                ref={inputRef}
                placeholder={turns.length > 0 ? t('chat.followUp') : t('chat.placeholder')}
                value={input}
                onChange={(e) => setInput(e.currentTarget.value)}
                onKeyDown={handleKeyDown}
                autosize
                minRows={1}
                maxRows={4}
                style={{ flex: 1 }}
                disabled={isStreaming}
              />
              <ActionIcon
                size="lg"
                variant="filled"
                onClick={handleSend}
                disabled={!input.trim() || isStreaming}
              >
                <IconSend size={18} />
              </ActionIcon>
            </Group>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}
